import type { QueryFunctionContext } from '@tanstack/react-query';

import type { ActionError, ActionResult } from './types.js';

type CreateQueryFnArgs<TData> = {
  defaultValue?: (error: ActionError) => TData;
};

/**
 * Generic fetcher for Tanstack Query that calls the provided async function with queryKey arguments.
 * The provided function should be a action result function.
 * This function should be used for type safety so that the queryFn doesn't diverge from the queryKeys.
 *
 * @template TArgs Arguments tuple type
 * @template TResult Return type of the function
 * @param fn The async function to call
 * @returns A fetcher function compatible with Tanstack Query
 */
export function createQueryFn<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<ActionResult<TResult>>
) {
  return (obj: CreateQueryFnArgs<TResult> = {}) => {
    return async ({ queryKey }: QueryFunctionContext<Readonly<TArgs>>): Promise<TResult> => {
      const result = await fn(...queryKey);
      if (result.status === 'ERROR') {
        if (obj.defaultValue) {
          return obj.defaultValue(result);
        }
        throw new Error(result.error);
      }

      return result.data;
    };
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ExtractActionReturnType<TFunction extends (...args: any) => any> = Extract<
  Awaited<ReturnType<TFunction>>,
  { data: object }
>['data'];
